import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { usePublishToEmma } from '../hooks/usePublishToEmma';
import portalT from '../translations/portal';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { ArrowLeft, ClipboardList, Send, Loader, Lightbulb, Plus, CheckCircle, Users, Calendar, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const MeetingBrain = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { lang } = useLanguage();
  const t = portalT[lang];
  const tp = t.toolPages.meeting;
  const chatEndRef = useRef(null);
  const [view, setView] = useState('start');
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [input, setInput] = useState('');
  const [notesText, setNotesText] = useState('');
  const [meetingData, setMeetingData] = useState(null);
  
  const lastAssistant = (Array.isArray(messages) ? messages : []).filter(m => m.role === 'assistant').slice(-1)[0]?.content;
  usePublishToEmma('AI Meeting Brain', lastAssistant);
  const headers = { Authorization: `Bearer ${token}` };

  const analyzeMeeting = async (text) => {
    if (!((text) == null ? '' : String(text)).trim()) return;
    setLoading(true); setView('result');
    try {
      const res = await axios.post(`${API}/compass/meeting/analyze`, { notes: text, language: lang }, { headers });
      setSessionId(res.data.session_id); setMeetingData(res.data.meeting_data);
      setMessages([{ role: 'user', content: text }, { role: 'assistant', content: res.data.analysis }]);
    } catch (e) { setMessages([{ role: 'assistant', content: lang === 'en' ? 'Analysis failed. Please check your API configuration.' : 'Analyse mislukt. Controleer je API-configuratie.' }]); }
    finally { setLoading(false); }
  };

  const sendChat = async (text) => {
    if (!((text) == null ? '' : String(text)).trim() || loading || !sessionId) return;
    setMessages(prev => [...prev, { role: 'user', content: text }]); setInput(''); setLoading(true);
    try {
      const res = await axios.post(`${API}/compass/meeting/chat`, { message: text, session_id: sessionId, language: lang }, { headers });
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch (e) { setMessages(prev => [...prev, { role: 'assistant', content: lang === 'en' ? 'Error. Please try again.' : 'Fout. Probeer het opnieuw.' }]); }
    finally { setLoading(false); }
  };

  const startNew = () => { setView('start'); setMessages([]); setSessionId(null); setNotesText(''); setMeetingData(null); };

  if (view === 'start') {
    return (
      <div className="tool-page">
        <div className="tool-header">
          <button onClick={() => navigate('/portal/ai-tools')} className="btn-secondary"><ArrowLeft size={16} /> {t.common.aiTools}</button>
          <h1 className="heading-1"><ClipboardList size={22} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 8 }} /> {tp.title}</h1>
        </div>
        <div className="tool-content">
          <div className="auto-start-section" data-testid="meeting-start">
            <div className="auto-input-card">
              <div className="auto-input-header"><ClipboardList size={20} /><h3 className="heading-3">{tp.inputLabel}</h3></div>
              <p className="body-small text-secondary">{tp.inputDesc}</p>
              <textarea className="form-input auto-textarea" style={{ minHeight: '180px' }}
                placeholder={tp.analyzePlaceholder}
                value={notesText} onChange={(e) => setNotesText(e.target.value)} rows={8} data-testid="meeting-textarea" />
              <button className="btn-primary" style={{ width: '100%', marginTop: '1rem' }} onClick={() => analyzeMeeting(notesText)} disabled={!((notesText) == null ? '' : String(notesText)).trim()} data-testid="meeting-analyze-btn">
                <ClipboardList size={16} /> {tp.analyzeBtn}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="compass-coach-page" data-testid="meeting-workspace">
      <aside className="compass-sidebar">
        <button onClick={() => navigate('/portal/ai-tools')} className="btn-secondary" style={{ width: '100%', justifyContent: 'center' }}><ArrowLeft size={14} /> {t.common.aiTools}</button>
        <div className="compass-role-badge" style={{ background: 'var(--accent-purple-200)' }}><ClipboardList size={16} /><span className="body-small" style={{ fontWeight: 500 }}>{tp.title}</span></div>
        {meetingData && (
          <div className="sim-sidebar-stats" data-testid="meeting-stats">
            {meetingData.decisions && <div className="sim-stat-item"><CheckCircle size={14} /><span className="body-small">{lang === 'nl' ? 'Besluiten' : 'Decisions'}</span><span className="body-small" style={{ fontWeight: 600 }}>{meetingData.decisions.length}</span></div>}
            {meetingData.action_items && <div className="sim-stat-item"><AlertCircle size={14} /><span className="body-small">{lang === 'nl' ? 'Acties' : 'Actions'}</span><span className="body-small" style={{ fontWeight: 600 }}>{meetingData.action_items.length}</span></div>}
            {meetingData.follow_ups && <div className="sim-stat-item"><Calendar size={14} /><span className="body-small">{lang === 'nl' ? 'Follow-ups' : 'Follow-ups'}</span><span className="body-small" style={{ fontWeight: 600 }}>{meetingData.follow_ups.length}</span></div>}
          </div>
        )}
        <button className="compass-new-chat" onClick={startNew}><Plus size={14} /> {lang === 'nl' ? 'Nieuwe Vergadering' : 'New Meeting'}</button>
      </aside>
      <main className="compass-main">
        <div className="compass-chat-container">
          {meetingData && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '0.75rem', padding: '1.25rem 0 0' }} data-testid="meeting-cards">
              {meetingData.action_items?.length > 0 && (
                <div className="sim-outcome-card" style={{ borderTopColor: 'var(--accent-orange-200)' }}>
                  <span className="caption"><AlertCircle size={12} style={{ display: 'inline', marginRight: 4 }} />{lang === 'nl' ? 'ACTIEPUNTEN' : 'ACTION ITEMS'}</span>
                  {meetingData.action_items.slice(0, 4).map((a, i) => (
                    <div key={i} className="body-small"><strong>{a.assignee}:</strong> {a.task} <span className="caption">({a.priority})</span></div>
                  ))}
                </div>
              )}
              {meetingData.decisions?.length > 0 && (
                <div className="sim-outcome-card" style={{ borderTopColor: 'var(--accent-green-200)' }}>
                  <span className="caption"><CheckCircle size={12} style={{ display: 'inline', marginRight: 4 }} />{lang === 'nl' ? 'BESLUITEN' : 'DECISIONS'}</span>
                  {meetingData.decisions.slice(0, 4).map((d, i) => (
                    <div key={i} className="body-small">{d.decision}</div>
                  ))}
                </div>
              )}
              {meetingData.follow_ups?.length > 0 && (
                <div className="sim-outcome-card" style={{ borderTopColor: 'var(--accent-blue-200)' }}>
                  <span className="caption"><Calendar size={12} style={{ display: 'inline', marginRight: 4 }} />{lang === 'nl' ? 'OPVOLGING' : 'FOLLOW-UPS'}</span>
                  {meetingData.follow_ups.slice(0, 4).map((f, i) => (
                    <div key={i} className="body-small"><strong>{f.owner}:</strong> {f.topic} <span className="caption">({f.date})</span></div>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className="compass-chat-messages" data-testid="meeting-messages">
            {loading && messages.length === 0 && <div className="auto-analyzing-state"><Loader className="spin" size={32} /><h3 className="heading-3">{lang === 'nl' ? 'Acties & besluiten extraheren...' : 'Extracting actions & decisions...'}</h3></div>}
            {(Array.isArray(messages) ? messages : []).map((msg, i) => (
              <div key={i} className={`compass-msg compass-msg-${msg.role}`}>
                {msg.role === 'assistant' ? <div className="compass-msg-content markdown-content"><ReactMarkdown>{msg.content}</ReactMarkdown></div> : <div className="compass-msg-content">{msg.content}</div>}
              </div>
            ))}
            {loading && messages.length > 0 && <div className="compass-msg compass-msg-assistant"><div className="compass-msg-content compass-typing"><Loader className="spin" size={16} /> {lang === 'nl' ? 'Verwerken...' : 'Processing...'}</div></div>}
            <div ref={chatEndRef} />
          </div>
          {!loading && messages.length > 0 && messages.length <= 2 && (
            <div className="auto-followup-bar">
              {(lang === 'nl' ? ["Maak een e-mail samenvatting voor deelnemers", "Prioriteer de actiepunten op urgentie", "Wat is onopgelost gebleven?"] : ["Create an email summary for attendees", "Prioritize the action items by urgency", "What was left unresolved?"]).map((s, i) => (
                <button key={i} className="compass-suggestion-btn" onClick={() => sendChat(s)}><Lightbulb size={14} /> {s}</button>
              ))}
            </div>
          )}
          <form className="compass-chat-input" onSubmit={(e) => { e.preventDefault(); sendChat(input); }}>
            <input type="text" value={input} onChange={(e) => setInput(e.target.value)} placeholder={tp.chatPlaceholder} disabled={loading} className="form-input" data-testid="meeting-input" />
            <button type="submit" disabled={loading || !((input) == null ? '' : String(input)).trim()} className="btn-primary compass-send-btn"><Send size={16} /></button>
          </form>
        </div>
      </main>
    </div>
  );
};

export default MeetingBrain;
